import { CMSData } from "@/cms/models";
import { z } from "zod";

export const CONTACT_INITIAL_STATE = {
  zodErrors: null,
  apiErrors: null,
  data: {
    firstName: "",
    lastName: "",
    address: "",
    zip: "",
    city: "",
    phone: "",
    email: "",
    message: "",
  },
};

export const DOWNLOAD_INITIAL_STATE = {
  zodErrors: null,
  apiErrors: null,
  data: {
    firstName: "",
    lastName: "",
    address: "",
    zip: "",
    city: "",
    phone: "",
    email: "",
    message: "",
  },
};

export const WISH_INITIAL_STATE = {
  zodErrors: null,
  apiErrors: null,
  data: {
    firstName: "",
    lastName: "",
    address: "",
    zip: "",
    city: "",
    phone: "",
    email: "",
    message: "",
  },
};

export const ASSESSMENT_INITIAL_STATE = {
  zodErrors: null,
  apiErrors: null,
  data: {
    time: "",
    firstName: "",
    lastName: "",
    address: "",
    zip: "",
    city: "",
    phone: "",
    email: "",
    message: "",
    date: new Date().toISOString().substring(0,10),
  },
};

export const VIEWING_INITIAL_STATE = {
  zodErrors: null,
  apiErrors: null,
  data: {
    time: "",
    firstName: "",
    lastName: "",
    address: "",
    zip: "",
    city: "",
    phone: "",
    email: "",
    message: "",
    date: new Date().toISOString().substring(0,10),
  },
};

export const OPEN_HOUSE_INITIAL_STATE = {
  zodErrors: null,
  apiErrors: null,
  data: {
    time: "",
    firstName: "",
    lastName: "",
    address: "",
    zip: "",
    city: "",
    phone: "",
    email: "",
    message: "",
  },
};

export const SEND_BID_INITIAL_STATE = {
  zodErrors: null,
  apiErrors: null,
  data: {
    priceValue: undefined,
    firstName: "",
    lastName: "",
    address: "",
    zip: "",
    city: "",
    phone: "",
    email: "",
    message: "",
  },
};

export const AUTH_INITIAL_STATE = {
  zodErrors: null,
  apiErrors: null,
  data: {
    password: ""
  },
};


export const contactSchema = (cmsData: CMSData) =>
  z.object({
    firstName: cmsData?.forms?.nameRequired
      ? z.string().min(1, "Fornavn er påkrævet")
      : z.string().nullable().optional(),
    lastName: cmsData?.forms?.nameRequired
      ? z.string().min(1, "Efternavn er påkrævet")
      : z.string().nullable().optional(),
    address: cmsData?.forms?.addressRequired
      ? z.string().min(1, "Adresse er påkrævet")
      : z.string().nullable().optional(),
    zip: cmsData?.forms?.zipRequired
      ? z.string().min(1, "Postnummer er påkrævet")
      : z.string().nullable().optional(),
    city: cmsData?.forms?.zipRequired
      ? z.string().min(1, "By er påkrævet")
      : z.string().nullable().optional(),
    phone: cmsData?.forms?.phoneRequired
      ? z
          .string()
          .min(1)
          .regex(/^\+?[1-9]\d{1,14}$/, "Telefonnummer er ikke gyldig")
      : z.union([
          z.literal(""),
          z.string().nullable(),
          z
            .string()
            .regex(/^\+?[1-9]\d{1,14}$/, "Telefonnummer er ikke gyldig"),
        ]),
    email: cmsData?.forms?.emailRequired
      ? z
          .string()
          .min(1, "E-mail er påkrævet")
          .email("E-mail adressen er ikke gyldig")
      : z.union([
          z.literal(""),
          z.string().nullable(),
          z.string().email("E-mail adressen er ikke gyldig"),
        ]),
  });

export const wishOptions = (specifics: any) => [
  {
    id: "villa",
    text: "Villa",
    selected: false,
    isHidden: !specifics.villa,
    groupId: "Bolig",
    disabled: false,
    wishNamespace: "dk.realequity.wish.boligønske",
    estateTypeNamespace: "dk.realequity.estate.villa",
  },
  {
    id: "raekkehus",
    text: "Rækkehus",
    selected: false,
    isHidden: !specifics.raekkehus,
    groupId: "Bolig",
    wishNamespace: "dk.realequity.wish.boligønske",
    estateTypeNamespace: "dk.realequity.estate.raekkehus",
    disabled: false,
  },
  {
    id: "ejerlejlighed",
    text: "Ejerlejlighed",
    selected: false,
    isHidden: !specifics.ejerlejlighed,
    groupId: "Bolig",
    wishNamespace: "dk.realequity.wish.boligønske",
    estateTypeNamespace: "dk.realequity.estate.ejerlejlighed",
    disabled: false,
  },
  {
    id: "fritidsbolig",
    text: "Fritidsbolig",
    selected: false,
    isHidden: !specifics.fritidsbolig,
    groupId: "Bolig",
    wishNamespace: "dk.realequity.wish.boligønske",
    estateTypeNamespace: "dk.realequity.estate.fritidshus",
    disabled: false,
  },
  {
    id: "andelsbolig",
    text: "Andelsbolig",
    selected: false,
    isHidden: !specifics.andelsbolig,
    groupId: "Bolig",
    wishNamespace: "dk.realequity.wish.boligønske",
    estateTypeNamespace: "dk.realequity.estate.andelsbolig",
    disabled: false,
  },
  {
    id: "landejendom",
    text: "Landejendom",
    selected: false,
    isHidden: !specifics.landejendom,
    groupId: "Bolig",
    wishNamespace: "dk.realequity.wish.boligønske",
    estateTypeNamespace: "dk.realequity.estate.landbrugsejendom",
    disabled: false,
  },
  {
    id: "helaarsgrund",
    text: "Helårsgrund",
    selected: false,
    isHidden: !specifics.helaarsgrund,
    groupId: "Bolig",
    wishNamespace: "dk.realequity.wish.boligønske",
    estateTypeNamespace: "dk.realequity.estate.helaarsgrund",
    disabled: false,
  },
  {
    id: "fritidsgrund",
    text: "Fritidsgrund",
    selected: false,
    isHidden: !specifics.fritidsgrund,
    groupId: "Bolig",
    wishNamespace: "dk.realequity.wish.boligønske",
    estateTypeNamespace: "dk.realequity.estate.fritidsgrund",
    disabled: false,
  },
  {
    id: "villalejlighed",
    text: "Villalejlighed",
    selected: false,
    isHidden: !specifics.villalejlighed,
    groupId: "Bolig",
    wishNamespace: "dk.realequity.wish.boligønske",
    estateTypeNamespace: "dk.realequity.estate.villalejlighed",
    disabled: false,
  },
  {
    id: "kvaeggaard",
    text: "Kvæggårde",
    selected: false,
    isHidden: !specifics.kvaeggaard,
    groupId: "Landbrug",
    disabled: false,
    wishNamespace: "dk.realequity.wish.landbrug",
    estateTypeNamespace: "dk.realequity.estate.kvaegbrug",
  },
  {
    id: "grisegaard",
    text: "Grisegårde",
    selected: false,
    isHidden: !specifics.svinegaard,
    groupId: "Landbrug",
    disabled: false,
    wishNamespace: "dk.realequity.wish.landbrug",
    estateTypeNamespace: "dk.realequity.estate.svineproduktion",
  },
  {
    id: "planteavlgard",
    text: "Planteavlgårde / Landbrugsjord",
    selected: false,
    isHidden: !specifics.planteavlgaard,
    groupId: "Landbrug",
    disabled: false,
    wishNamespace: "dk.realequity.wish.landbrug",
    estateTypeNamespace: "dk.realequity.estate.planteavl",
  },
  {
    id: "specialejendom",
    text: "Specialejendomme",
    selected: false,
    isHidden: !specifics.specialejendom,
    groupId: "Landbrug",
    disabled: false,
    wishNamespace: "dk.realequity.wish.landbrug",
    estateTypeNamespace: "dk.realequity.estate.speciallandbrug",
  },
  {
    id: "skovejendom",
    text: "Skovejendomme",
    selected: false,
    isHidden: !specifics.skovejendom,
    groupId: "Landbrug",
    disabled: false,
    wishNamespace: "dk.realequity.wish.landbrug",
    estateTypeNamespace: "dk.realequity.estate.skovnaturejendom",
  },
  {
    id: "koeb",
    text: "Køb",
    selected: false,
    isHidden: !specifics.koeb,
    groupId: "Erhverv",
    disabled: false,
    wishNamespace: "dk.realequity.wish.erhverv",
    estateTypeNamespace: null,
  },
  {
    id: "leje",
    text: "Leje",
    selected: false,
    isHidden: !specifics.leje,
    groupId: "Erhverv",
    disabled: false,
    wishNamespace: "dk.realequity.wish.erhvervslejemaaloenske",
    estateTypeNamespace: null,
  },
];
